import hireBg from '@assets/images/hire-r8tr-images/bg.webp';
import { parseAddressComponents } from '@components/forms/address-search-field/parseAddressComponents';
import { useHireJob } from '@features/buyer/hire/hire-job-context';
import {
  UpdateHireAddressFields,
  UpdateHireJobFields,
  UpdateHirePersonalFields,
  UpdateHirePriceFields,
} from '@features/buyer/hire/update-hire-r8tr';
import { apiErrorMessage } from '@lib/api/apiError';
import { useCategoriesQuery } from '@lib/api/categories';
import { fetchBuyerCost } from '@lib/api/costs';
import { useScrappedItemQuery } from '@lib/api/jobs';
import { Box, Container } from '@mui/material';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import type {
  Category,
  HireFormValues,
  ScrappedItemDetails,
  StoredUser,
} from './hireR8trTypes';


const HireR8tr = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { saveJob } = useHireJob();
  const ebayUrl = new URL(window.location.href).searchParams;

  const itemId = ebayUrl.get('itemID');
  const [selectedOption, setSelectedOption] = useState<{
    category: string | number;
    subCategory: string | number;
  }>({
    category: '',
    subCategory: '',
  });
  const [hireR8trObject, setHireR8trObject] = useState<HireFormValues>({});
  const [buyerCostState, setBuyerCostState] = useState<number | null>(null);
  const [buyerCostError, setBuyerCostError] = useState('');
  const [isBuyerCostLoading, setIsBuyerCostLoading] = useState(false);
  const [jobLatitude, setJobLatitude] = useState<number | null>(null);
  const [jobLongitude, setJobLongitude] = useState<number | null>(null);
  const [r8tedVehicleAddress, setR8tedVehicleAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [checked, setChecked] = useState(false);
  const [itemIdDataObject, setItemIdDataObject] = useState<
    ScrappedItemDetails | undefined
  >(undefined);
  const { data: categoryData } = useCategoriesQuery();
  const allCategories = (categoryData ?? []) as Category[];
  const { data: scrappedData } = useScrappedItemQuery(itemId);

  useEffect(() => {
    const scrapped = scrappedData as { data?: ScrappedItemDetails } | undefined;
    if (scrapped?.data) {
      setItemIdDataObject(scrapped.data);
    }
  }, [scrappedData]);

  useEffect(() => {
    if (
      selectedOption.category === '' ||
      itemIdDataObject?.askingPrice == null ||
      itemIdDataObject?.askingPrice === ''
    ) {
      setBuyerCostState(null);
      setBuyerCostError('');
      setIsBuyerCostLoading(false);
      return;
    }

    const parsedPrice = Number(itemIdDataObject?.askingPrice);
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      setBuyerCostState(null);
      setBuyerCostError(t('hire.invalidAskingPrice'));
      setIsBuyerCostLoading(false);
      return;
    }

    setIsBuyerCostLoading(true);
    setBuyerCostError('');
    setBuyerCostState(null);

    const timeoutId = setTimeout(() => {
      void (async () => {
        try {
          const nextBuyerCost = await fetchBuyerCost(
            selectedOption.category,
            parsedPrice
          );
          setBuyerCostState(nextBuyerCost);
        } catch (error: unknown) {
          setBuyerCostState(null);
          setBuyerCostError(
            apiErrorMessage(error) || t('hire.unableToCalculateFeeRetry')
          );
        } finally {
          setIsBuyerCostLoading(false);
        }
      })();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [selectedOption.category, itemIdDataObject?.askingPrice, t]);

  const handleChangeObject = (event: ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();
    setHireR8trObject({
      ...hireR8trObject,
      [event.target.name]: event.target.value,
    });
  };

  const handleItemDataChangeObject = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    if (!itemIdDataObject) return;
    const tempItemIdDataObject = { ...itemIdDataObject };
    const field = event.target.name;
    if (
      field === 'askingPrice' ||
      field === 'vIN' ||
      field === 'year' ||
      field === 'make' ||
      field === 'model' ||
      field === 'title'
    ) {
      tempItemIdDataObject[field] = event.target.value;
    }
    setItemIdDataObject(tempItemIdDataObject);
  };

  const handleCheckBoxChange = (): void => {
    setChecked(!checked);
  };

  const handleChange = (event: { target: { value: unknown } }): void => {
    if (typeof event.target.value !== 'string') return;
    const parsed = JSON.parse(event.target.value) as {
      category?: string | number;
      subCategory?: string | number;
    };
    setSelectedOption({
      category: parsed.category ?? '',
      subCategory: parsed.subCategory ?? '',
    });
  };

  const handleSelectAddress = async (jobAddress: string): Promise<void> => {
    if (!jobAddress) return;
    try {
      const results = await geocodeByAddress(jobAddress);
      const latLng = await getLatLng(results[0]);
      const parsed = parseAddressComponents(results[0]);
      setState(parsed.state);
      setCity(parsed.city);
      setR8tedVehicleAddress(jobAddress);
      setJobLatitude(latLng.lat);
      setJobLongitude(latLng.lng);
    } catch {
      setJobLatitude(null);
      setJobLongitude(null);
    }
  };

  const handleChangeAddress = (address: string): void => {
    setR8tedVehicleAddress(address);
    setJobLatitude(null);
    setJobLongitude(null);
  };

  const handleSubmit = async (): Promise<void> => {
    if (
      !hireR8trObject.name ||
      !hireR8trObject.email ||
      !hireR8trObject.email.includes('@') ||
      checked != true ||
      !selectedOption.category ||
      !selectedOption.subCategory ||
      !itemIdDataObject?.askingPrice ||
      !itemIdDataObject.vIN ||
      !itemIdDataObject.year ||
      !itemIdDataObject.make ||
      !itemIdDataObject.model ||
      !hireR8trObject.description ||
      !hireR8trObject.zipCode ||
      Number(hireR8trObject.zipCode) < 1
    ) {
      toast.error(String(t('hire.fillAllFields')));
      return;
    }

    if (isBuyerCostLoading) {
      toast.error(String(t('hire.waitForFee')));
      return;
    }

    if (buyerCostError || buyerCostState === null) {
      toast.error(
        buyerCostError || String(t('hire.feeUnavailable'))
      );
      return;
    }

    let latitude = jobLatitude;
    let longitude = jobLongitude;
    if (!r8tedVehicleAddress || !city || !state) {
      toast.error(
        String(t('hire.invalidAddress', { address: r8tedVehicleAddress }))
      );
      return;
    }
    if (!latitude || !longitude) {
      try {
        const results = await geocodeByAddress(r8tedVehicleAddress);
        const latLng = await getLatLng(results[0]);
        latitude = latLng.lat;
        longitude = latLng.lng;
      } catch {
        toast.error(
          String(t('hire.invalidAddress', { address: r8tedVehicleAddress }))
        );
        return;
      }
    }
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? (JSON.parse(storedUser) as StoredUser) : null;
    const finalObject = {
      categoryId: selectedOption.category,
      subCategoryId: selectedOption.subCategory,
      askingPrice: String(Number(itemIdDataObject.askingPrice)),
      buyerCost: String(buyerCostState),
      description: hireR8trObject.description,
      vinOrSerial: itemIdDataObject.vIN,
      make: itemIdDataObject.make,
      year: String(itemIdDataObject.year),
      model: itemIdDataObject.model,
      address: r8tedVehicleAddress,
      city: city,
      state: state,
      zip: hireR8trObject.zipCode,
      longitude: jobLongitude,
      latitude: jobLatitude,
      contactName: hireR8trObject.name,
      contactNumber: user?.user?.phoneNumber ?? '',
      contactEmail: hireR8trObject.email,
      isScrappedJob: true,
    };

    saveJob(finalObject);
    if (itemId) {
      navigate(`/submit-payment?itemID=${itemId}`);
    } else {
      navigate('/submit-payment');
    }
  };

  const { name, email, description, zipCode } = hireR8trObject;

  return (
    <Box
      sx={{
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
        backgroundImage: `url(${hireBg})`,
      }}
    >
      <Container
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            mb: '50px',
            mt: '50px',
            width: { xs: '100%', sm: '50%' },
            bgcolor: 'text.primary',
            clipPath: 'polygon(0 0, 0 100%, 100% 100%, 100% 20%, 80% 0)',
          }}
        >
          <ValidatorForm
            style={{
              width: '50% !important',
              margin: '100px 50px 50px 50px',
            }}
            onSubmit={() => {
              void handleSubmit();
            }}
          >
            <UpdateHirePersonalFields
              name={name}
              email={email}
              handleChangeObject={handleChangeObject}
            />
            <UpdateHirePriceFields
              selectedOption={selectedOption}
              handleChange={handleChange}
              allCategories={allCategories}
              itemIdDataObject={itemIdDataObject}
              handleItemDataChangeObject={handleItemDataChangeObject}
              buyerCostState={buyerCostState}
              isBuyerCostLoading={isBuyerCostLoading}
              buyerCostError={buyerCostError}
            />
            <UpdateHireJobFields
              itemIdDataObject={itemIdDataObject}
              handleItemDataChangeObject={handleItemDataChangeObject}
              description={description}
              handleChangeObject={handleChangeObject}
            />
            <UpdateHireAddressFields
              r8tedVehicleAddress={r8tedVehicleAddress}
              handleChangeAddress={handleChangeAddress}
              handleSelectAddress={handleSelectAddress}
              zipCode={zipCode}
              handleChangeObject={handleChangeObject}
              checked={checked}
              handleCheckBoxChange={handleCheckBoxChange}
            />
          </ValidatorForm>
        </Box>
      </Container>
    </Box>
  );
};

export default HireR8tr;
